import { getConfig } from '@superbet-group/web.lib.config';
import { Country as CountryCode } from '@superbet-group/web.types';

import {
    computed,
    Ref,
    ref,
} from '@vue/composition-api';
import Country from '@core/models/country/Country';

const countryCode = getConfig().countrySpecific.country;

type UsePhoneParserComposable = {
    prefix: Ref<string>;
    localNumber: Ref<string>;
    formattedPhoneNumber: Ref<string>;
    rawFullPhoneNumber: Ref<string>;
    parsePhoneNumber: (phone: string) => void;
    formatApiValidPhoneNumber: (prefix: string, localNumber: string) => string;
};

export function usePhoneParser(allowedCountries: Ref<Country[]> = ref([])): UsePhoneParserComposable {
    const preferredPrefix = getConfig().countrySpecific.user.settings.defaultCountryPrefix;
    const prefix = ref('');
    const localNumber = ref('');

    function extractPrefix(fullNumber: string): void {
        const phoneNumberCountry = allowedCountries.value
            // some prefixes start with the exact same digits
            .filter((country) => fullNumber.startsWith(country.prefix))
            // select the longest prefix
            .sort((a, b) => b.prefix.length - a.prefix.length)[0];
        prefix.value = phoneNumberCountry?.prefix || preferredPrefix;
    }

    function parsePhoneNumber(fullNumber: string): void {
        extractPrefix(fullNumber);
        switch (countryCode) {
            case CountryCode.Ro:
                // RO numbers must be stored starting with 0 and user must input them starting with 7
                localNumber.value = fullNumber.startsWith('0') ? fullNumber.substring(1) : fullNumber;
                break;
            case CountryCode.Pl:
                // PL numbers must be stored and inputed without the prefix
                localNumber.value = fullNumber.startsWith(preferredPrefix) ? fullNumber.substring(preferredPrefix.length) : fullNumber;
                break;
            default:
                localNumber.value = fullNumber.startsWith(prefix.value) ? fullNumber.substring(prefix.value.length) : fullNumber;
                break;
        }
    }

    function formatApiValidPhoneNumber(prefix: string, localNumber: string): string {
        switch (countryCode) {
            case CountryCode.Ro:
                // RO numbers must be stored starting with 0
                return `0${localNumber}`;
            case CountryCode.Pl:
                // PL numbers must be stored and inputed without the prefix
                return `${localNumber}`;
            default:
                return `${prefix}${localNumber}`;
        }
    }

    return {
        prefix,
        localNumber,
        formattedPhoneNumber: computed(() => formatApiValidPhoneNumber(prefix.value, localNumber.value)),
        rawFullPhoneNumber: computed(() => `${prefix.value}${localNumber.value}`),
        parsePhoneNumber,
        formatApiValidPhoneNumber,
    };
}
